import { useCallback, useEffect, useRef, useState } from 'react';

type FullscreenSet = ReadonlySet<string> | Set<string>;

/**
 * パネルの全画面表示を管理するフック
 */
export const usePanelFullscreen = (eligiblePanelIds: FullscreenSet) => {
    const [fullScreenPanelId, setFullScreenPanelId] = useState<string | null>(null);
    const previousBodyOverflow = useRef<string | null>(null);

    const exitFullScreen = useCallback(() => {
        setFullScreenPanelId(null);
    }, []);

    const enterFullScreen = useCallback(
        (panelId: string) => {
            if (!eligiblePanelIds.has(panelId)) return;
            setFullScreenPanelId(panelId);
        },
        [eligiblePanelIds],
    );

    useEffect(() => {
        if (!fullScreenPanelId) {
            if (previousBodyOverflow.current !== null) {
                document.body.style.overflow = previousBodyOverflow.current;
                previousBodyOverflow.current = null;
            }
            return;
        }

        const originalOverflow = document.body.style.overflow;
        previousBodyOverflow.current = originalOverflow;
        document.body.style.overflow = 'hidden';

        return () => {
            document.body.style.overflow = originalOverflow;
            previousBodyOverflow.current = null;
        };
    }, [fullScreenPanelId]);

    useEffect(() => {
        if (!fullScreenPanelId) return;

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                exitFullScreen();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [exitFullScreen, fullScreenPanelId]);

    return {
        fullScreenPanelId,
        enterFullScreen,
        exitFullScreen,
    };
};
